import { useCallback, useState } from 'react';
import axios from 'axios';
import uploadsApiClient from '../../../api/uploads';
import useHandleErrorMutation from '../../../hooks/useHandleErrorMutation';
import { KlydoError } from '../../../hooks/types';

type UseSectionMediaUploadOptions = {
  onUploaded: (url: string) => void;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
};

const useSectionMediaUpload = ({
  onUploaded,
  onUploadStart,
  onUploadEnd,
}: UseSectionMediaUploadOptions) => {
  const [isUploading, setIsUploading] = useState(false);
  const handleError = useHandleErrorMutation();

  const handleFileSelected = useCallback(
    async (file: File) => {
      setIsUploading(true);
      onUploadStart?.();
      try {
        const { signedUrl, fileKey } =
          await uploadsApiClient.generateSectionMediaSignedUrl({
            fileName: file.name,
          });

        await axios.put(signedUrl, file, {
          headers: { 'Content-Type': file.type || 'application/octet-stream' },
        });

        const response = await uploadsApiClient.uploadSectionMedia({
          file: fileKey,
          options: { resource_type: 'auto' },
        });

        onUploaded(response.url);
      } catch (error) {
        handleError(error as KlydoError, 'Failed to upload media');
      } finally {
        setIsUploading(false);
        onUploadEnd?.();
      }
    },
    [onUploaded, onUploadStart, onUploadEnd, handleError],
  );

  return { handleFileSelected, isUploading };
};

export default useSectionMediaUpload;
